import type {IssueFieldSelectionValues, IssueFieldType} from '@github-ui/issue-fields-shared/issue-field'

import {VALUES} from '../constants/values'
import {DisplayMode} from './display-mode'
import type {IssueCreateArguments} from './template-args'

export type IssueCreateContext = 'full_page' | 'modal' | 'copilot_immersive'
export function deriveIssueCreateContext(optionConfig: SafeOptionConfig): IssueCreateContext {
  if (optionConfig.isCopilotImmersive) return 'copilot_immersive'
  if (optionConfig.insidePortal) return 'modal'
  return 'full_page'
}

type ScopedAssignee = {
  avatarUrl: string
  login: string
}

export type ScopedField = {
  fieldId: string
  fieldName: string
  fieldType: IssueFieldType
  value: IssueFieldSelectionValues
}

export type UserSettingsOptionConfig = {
  emojiSkinTonePreference?: number
  copilotShowFunctionality?: boolean
}

// Separate out the options that are required to have default non-undefined values
type RequiredOptionConfig = {
  showFullScreenButton?: boolean
  navigateToFullScreenOnTemplateChoice?: boolean
  insidePortal?: boolean
  /**
   * Where the unsaved draft is kept. This is only used when no repository is chosen up
   * front — with one, the draft is kept under that repository's name instead, so the same
   * draft shows up everywhere its new issue form opens. Set `isolatedStorageKeyPrefix` when
   * a flow needs a draft of its own.
   */
  storageKeyPrefix?: string
  // When true, disables the Copilot Assignment Modal from opening when Copilot is assigned to an issue.
  // This should be set to true for draft issues since they don't exist yet and cross-repository task functionality doesn't apply.
  disableCopilotAssignmentModal?: boolean
  isCopilotImmersive?: boolean // Used for analytics and context tracking.
} & UserSettingsOptionConfig

type UndefinableOptionConfig = {
  dialogTitle?: string
  showRepositoryPicker?: boolean
  scopedOrganization?: string
  scopedProjectTitle?: string
  scopedIssueType?: string
  scopedMilestone?: string
  scopedAssignees?: ScopedAssignee[]
  scopedFields?: ScopedField[]
  issueCreateArguments?: IssueCreateArguments
  defaultDisplayMode?: DisplayMode
  emojiSkinTonePreference?: number
  canBypassTemplateSelection?: boolean
  navigate?: (url: string) => void
  hideCreateMore?: boolean
  /**
   * Gives an embedded flow its own draft namespace, outranking the repository prefix that a
   * preselected repository would otherwise impose. Use when the draft must not be shared with
   * (or destroy) the repository's own `/issues/new` draft.
   */
  isolatedStorageKeyPrefix?: string
}

// Additional helper properties
type OptionConfigProperties = {
  getDefaultDisplayMode: (fallback: DisplayMode | undefined) => DisplayMode
}

export type IssueCreateOptionConfig = RequiredOptionConfig & UndefinableOptionConfig

export type SafeOptionConfig = Readonly<
  Required<RequiredOptionConfig> & UndefinableOptionConfig & OptionConfigProperties
>

const OPTION_DEFAULT_CONFIG: Omit<SafeOptionConfig, keyof OptionConfigProperties> = {
  showFullScreenButton: false,
  navigateToFullScreenOnTemplateChoice: false,
  insidePortal: true, // Assume true given most cases are inside the dialog
  showRepositoryPicker: true,
  storageKeyPrefix: '',
  canBypassTemplateSelection: false,
  emojiSkinTonePreference: 0,
  copilotShowFunctionality: false,
  disableCopilotAssignmentModal: false,
  isCopilotImmersive: false,
  hideCreateMore: false,
}

// Draft-storage namespace, in precedence order: a flow that opted into isolation, then a
// preselected repository, then whatever the caller passed.
function resolveStorageKeyPrefix(config: IssueCreateOptionConfig): string {
  if (config.isolatedStorageKeyPrefix !== undefined) return config.isolatedStorageKeyPrefix

  const repository = config.issueCreateArguments?.repository
  if (repository) return `${repository.owner}-${repository.name}`

  return config.storageKeyPrefix ?? VALUES.storageKeyPrefixes.defaultFallback
}

// This method is used to ensure we only default on values that weren't explicitly specified.
export const getSafeConfig = (config: IssueCreateOptionConfig | undefined): SafeOptionConfig => {
  const filledConfig = {
    ...OPTION_DEFAULT_CONFIG,
    ...config,
  }

  // These are values we want to override before returning, as they take precedence over the default values
  // and depend on the values of other config properties.
  const overrides = {
    storageKeyPrefix: resolveStorageKeyPrefix(filledConfig),
  }

  const configProperties: OptionConfigProperties = {
    getDefaultDisplayMode: (fallback: DisplayMode | undefined) =>
      filledConfig.defaultDisplayMode ?? fallback ?? DisplayMode.TemplatePicker,
  }

  return {
    ...filledConfig,
    ...overrides,
    ...configProperties,
  }
}

export const getDefaultConfig = (): SafeOptionConfig => getSafeConfig(undefined)
